import { Box, Grid, makeStyles, Theme, Typography, useMediaQuery, useTheme } from "@material-ui/core";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import CancelIcon from "@material-ui/icons/Cancel";
import EventIcon from "@material-ui/icons/Event";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import { StatisticHomePage } from "models/auth";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export interface StatisticsProps {
  statistic: StatisticHomePage | undefined;
}

export default function Statistics({ statistic }: StatisticsProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints?.down("sm"));

  const useStyles = makeStyles((theme: Theme) => ({
    content: {
      borderRadius: 20,
      paddingBottom: 0,
      flexGrow: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      boxSizing: "border-box",
      [theme.breakpoints?.down("sm")]: {
        marginTop: 0,
        marginBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
      },
    },
    wrapper: {
      borderRadius: 18,
      padding: "12px 16px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      minHeight: 100,
      flexGrow: 1,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxShadow: "0 6px 16px rgba(0, 0, 0, 0.03)",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 12px 24px rgba(0, 0, 0, 0.08)",
      },
      [theme.breakpoints?.down("sm")]: {
        padding: "8px 12px",
        minHeight: 70,
        borderRadius: 12,
      },
      "& >h6": {
        textAlign: "left",
        fontSize: "0.8rem",
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.03em",
        marginBottom: 4,
        [theme.breakpoints?.down("sm")]: {
          fontSize: "0.725rem",
        },
      },
    },
    content__totalLesson: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-end",
      marginTop: 6,
      position: "relative",
    },
    vectorIconBox: {
      width: 46,
      height: 46,
      borderRadius: 14,
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      backdropFilter: "blur(8px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 4px 14px rgba(0, 0, 0, 0.06)",
      [theme.breakpoints?.down("sm")]: {
        width: 34,
        height: 34,
        borderRadius: 8,
      },
    },
  }));

  const classes = useStyles();
  const [difference, setDifference] = useState<any>();
  const { t } = useTranslation();

  useEffect(() => {
    const currentDay = new Date().getTime();
    const examDay = statistic && new Date(statistic.nextExam).getTime();
    const diff = moment.duration(moment(examDay).diff(moment(currentDay)));
    setDifference(diff);
  }, [statistic]);

  const handleDateChange = () => {
    if (difference?.days() > 0) {
      return (
        <Typography variant="body2" component="span" style={{ color: "#D97706", fontSize: isMobile ? "1.4rem" : "1.8rem", fontWeight: 800 }}>
          {difference?.days()} <span style={{ fontSize: isMobile ? "0.85rem" : "1rem", color: "#64748B", fontWeight: 700 }}>{t("home.statistic.ngay")}</span>
        </Typography>
      );
    } else if (difference?.days() === 0 && difference?.hours() > 0) {
      return (
        <Typography variant="body2" component="span" style={{ color: "#D97706", fontSize: isMobile ? "1.4rem" : "1.8rem", fontWeight: 800 }}>
          {difference?.hours()} <span style={{ fontSize: isMobile ? "0.85rem" : "1rem", color: "#64748B", fontWeight: 700 }}>{t("home.statistic.gio")}</span>
        </Typography>
      );
    } else if (difference?.days() === 0 && difference?.hours() === 0 && difference?.minutes() > 0) {
      return (
        <Typography variant="body2" component="span" style={{ color: "#D97706", fontSize: isMobile ? "1.4rem" : "1.8rem", fontWeight: 800 }}>
          {difference?.minutes()} <span style={{ fontSize: isMobile ? "0.85rem" : "1rem", color: "#64748B", fontWeight: 700 }}>{t("home.statistic.phut")}</span>
        </Typography>
      );
    } else {
      return (
        <Typography variant="body2" component="span" style={{ color: "#64748B", fontSize: isMobile ? "1.3rem" : "1.6rem", fontWeight: 800 }}>
          0 <span style={{ fontSize: isMobile ? "0.85rem" : "0.95rem", color: "#94A3B8", fontWeight: 700 }}>{t("home.statistic.bai")}</span>
        </Typography>
      );
    }
  };

  return (
    <Box className={classes.content}>
      <Grid container spacing={1} style={{ height: isMobile ? "auto" : "calc(100% + 8px)" }}>
        {/* Card 1: Bài Học Trong Tháng */}
        <Grid item xs={6} style={{ display: "flex", flexDirection: "column" }}>
          <Box className={classes.wrapper} style={{ backgroundColor: "#EFF6FF", border: "1px solid #BFDBFE" }}>
            <Typography variant="subtitle2" style={{ color: "#1E40AF" }}>
              {t("home.statistic.baihoctrongthang")}
            </Typography>
            <Box className={classes.content__totalLesson}>
              <Typography variant="body2" component="span" style={{ color: "#2563EB", fontSize: isMobile ? "1.4rem" : "1.8rem", fontWeight: 800 }}>
                {statistic?.noLessionInMonth || 0}
                <span style={{ fontSize: isMobile ? "0.85rem" : "0.9rem", color: "#60A5FA", fontWeight: 700, marginLeft: 4 }}>
                  {t("home.statistic.bai")}
                </span>
              </Typography>

              <Box className={classes.vectorIconBox}>
                <MenuBookIcon style={{ fontSize: isMobile ? 18 : 22, color: "#2563EB" }} />
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Card 2: Số Bài Đã Thi */}
        <Grid item xs={6} style={{ display: "flex", flexDirection: "column" }}>
          <Box className={classes.wrapper} style={{ backgroundColor: "#F0FDF4", border: "1px solid #BBF7D0" }}>
            <Typography variant="subtitle2" style={{ color: "#166534" }}>
              {t("home.statistic.sobaidathi")}
            </Typography>
            <Box className={classes.content__totalLesson}>
              <Typography variant="body2" component="span" style={{ color: "#16A34A", fontSize: isMobile ? "1.4rem" : "1.8rem", fontWeight: 800 }}>
                {statistic?.noExams || 0}
                <span style={{ fontSize: isMobile ? "0.85rem" : "0.9rem", color: "#4ADE80", fontWeight: 700, marginLeft: 4 }}>
                  {t("home.statistic.bai")}
                </span>
              </Typography>

              <Box className={classes.vectorIconBox}>
                <AssignmentTurnedInIcon style={{ fontSize: isMobile ? 18 : 22, color: "#16A34A" }} />
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Card 3: Số Bài Đã Bỏ Lỡ */}
        <Grid item xs={6} style={{ display: "flex", flexDirection: "column" }}>
          <Box className={classes.wrapper} style={{ backgroundColor: "#FEF2F2", border: "1px solid #FECACA" }}>
            <Typography variant="subtitle2" style={{ color: "#991B1B" }}>
              {t("home.statistic.sobaidabolo")}
            </Typography>
            <Box className={classes.content__totalLesson}>
              <Typography variant="body2" component="span" style={{ color: "#DC2626", fontSize: isMobile ? "1.4rem" : "1.8rem", fontWeight: 800 }}>
                {statistic?.noLessionMissed || 0}
                <span style={{ fontSize: isMobile ? "0.85rem" : "0.9rem", color: "#F87171", fontWeight: 700, marginLeft: 4 }}>
                  {t("home.statistic.bai")}
                </span>
              </Typography>

              <Box className={classes.vectorIconBox}>
                <CancelIcon style={{ fontSize: isMobile ? 18 : 22, color: "#DC2626" }} />
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Card 4: Lịch Kiểm Tra Sắp Tới */}
        <Grid item xs={6} style={{ display: "flex", flexDirection: "column" }}>
          <Box className={classes.wrapper} style={{ backgroundColor: "#FFFBEB", border: "1px solid #FDE68A" }}>
            <Typography variant="subtitle2" style={{ color: "#92400E" }}>
              {t("home.statistic.lichkiemtrasaptoi")}
            </Typography>
            <Box className={classes.content__totalLesson}>
              {handleDateChange()}

              <Box className={classes.vectorIconBox}>
                <EventIcon style={{ fontSize: isMobile ? 18 : 22, color: "#D97706" }} />
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
